import sparePartsApi, { ApiResponse } from "../../../../api/spareParts.api";

import {
  Pagination,
  SparePart,
  SparePartApiRow,
  SparePartFilters,
  SparePartListResponse,
  SparePartPayload,
} from "../types";

const BASE_URL = "/spare-parts";

const mapSparePart = (
  part: SparePartApiRow,
  stockByPartId = new Map<string, SparePart["hubStocks"]>()
): SparePart => {
  const hubStocks = stockByPartId.get(part.id) || [];
  const currentStock = hubStocks.reduce(
    (total, stock) => total + stock.availableStock,
    0
  );

  return {
  ...part,
  categoryName: part.category?.name || "-",
  currentStock,
  stockedHubCount: hubStocks.filter((stock) => stock.availableStock > 0).length,
  hubStockSummary: hubStocks.length
    ? hubStocks
        .map((stock) => `${stock.warehouseName}: ${stock.availableStock}`)
        .join(", ")
    : "No hub stock yet",
  hubStocks,
};
};

const getHubName = (hub: any) =>
  hub?.code ? `${hub.name} (${hub.code})` : hub?.name || hub?.id || "-";

class SparePartService {
  async getSpareParts(params?: SparePartFilters): Promise<SparePartListResponse> {
    const response = await sparePartsApi.get<ApiResponse<SparePartApiRow[]>>(
      BASE_URL,
      { params }
    );

    const [stocksResult, hubsResult] = await Promise.allSettled([
      sparePartsApi.get<ApiResponse<any[]>>("/inventory/stocks", {
        params: { page: 1, limit: 1000 },
      }),
      sparePartsApi.get<ApiResponse<any[]>>("/integrations/hubs"),
    ]);

    const stockRows =
      stocksResult.status === "fulfilled" ? stocksResult.value.data.data || [] : [];
    const hubRows =
      hubsResult.status === "fulfilled" ? hubsResult.value.data.data || [] : [];

    const hubsById = new Map(
      hubRows.map((hub: any) => [hub.id, getHubName(hub)])
    );

    const stockByPartId = new Map<string, SparePart["hubStocks"]>();

    stockRows.forEach((stock: any) => {
      const rows = stockByPartId.get(stock.sparePartId) || [];
      const availableStock = Number(stock.availableQuantity || 0);
      const reservedStock = Number(stock.reservedQuantity || 0);
      const damagedStock = Number(stock.damagedQuantity || 0);

      rows.push({
        warehouseId: stock.warehouseId,
        warehouseName:
          hubsById.get(stock.warehouseId) || stock.warehouseName || stock.warehouseId,
        availableStock,
        reservedStock,
        damagedStock,
        totalStock: availableStock + reservedStock + damagedStock,
      });

      stockByPartId.set(stock.sparePartId, rows);
    });

    return {
      items: (response.data.data || []).map((part) =>
        mapSparePart(part, stockByPartId)
      ),
      pagination: response.data.meta as Pagination,
    };
  }

  async getSparePart(id: string): Promise<SparePart> {
    const response = await sparePartsApi.get<ApiResponse<SparePartApiRow>>(
      `${BASE_URL}/${id}`
    );

    return mapSparePart(response.data.data);
  }

  async createSparePart(payload: SparePartPayload): Promise<SparePart> {
    const response = await sparePartsApi.post<ApiResponse<SparePartApiRow>>(
      BASE_URL,
      payload
    );

    return mapSparePart(response.data.data);
  }

  async updateSparePart(
    id: string,
    payload: SparePartPayload
  ): Promise<SparePart> {
    const response = await sparePartsApi.put<ApiResponse<SparePartApiRow>>(
      `${BASE_URL}/${id}`,
      payload
    );

    return mapSparePart(response.data.data);
  }

  async updateStatus(
    id: string,
    status: "ACTIVE" | "INACTIVE"
  ): Promise<void> {
    await sparePartsApi.patch(`${BASE_URL}/${id}/status`, { status });
  }

  async deleteSparePart(id: string): Promise<void> {
    await sparePartsApi.delete(`${BASE_URL}/${id}`);
  }
}

export default new SparePartService();
